// AuthProvider — sumber tunggal state login untuk seluruh app (menggantikan
// contexts/auth.tsx + login internal App.tsx yang lama). Login = pemilihan
// AKUN (username) + PIN (US-AUTH-1), bukan pemilihan peran.
//
// Permission efektif sudah dihitung server (rfc-004 §model) dan dikirim di
// account.permissions. UI hanya menyembunyikan kontrol (UX); server tetap
// otoritatif (US-AUTH-2 AC4).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { toast } from "@ds/ui";
import { login as apiLogin, refreshSession, setRefreshHandler, switchBranch as apiSwitchBranch } from "../api";
import { clearScreenUnlock, clearSession, loadSession, saveSession, touchScreenUnlock, type Session } from "../data/session";

type Account = Session["account"];
type Status = "loading" | "anonymous" | "authenticated";

type AuthValue = {
  status: Status;
  account: Account | null;
  token: string;
  isOwner: boolean;
  /** Cabang aktif sesi (RFC-007); "" bila mode single-cabang lama. */
  branchId: string;
  /** Boleh lintas cabang (Pemilik) — pemilih cabang menawarkan semua cabang. */
  branchViewAll: boolean;
  /** Kode cabang aktif (prefix struk), terisi oleh BranchProvider. */
  branchCode: string;
  /** Login dengan username (atau UUID) akun + PIN. */
  signIn: (username: string, pin: string) => Promise<Session>;
  /** Keluar penuh: hapus sesi. */
  signOut: () => void;
  /** Ganti cabang aktif: panggil server, simpan token baru, reload data. */
  switchBranch: (branchId: string) => Promise<void>;
  /** Lengkapi kode cabang aktif setelah daftar cabang dimuat (BranchProvider). */
  setBranchCode: (code: string) => void;
  /** True bila akun punya permission efektif tsb (owner selalu true). */
  can: (permission: string) => boolean;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    const restored = loadSession();
    if (restored) {
      setSession(restored);
      setStatus("authenticated");
    } else {
      setStatus("anonymous");
    }
  }, []);

  const signIn = useCallback(async (username: string, pin: string) => {
    const result = await apiLogin(username.trim(), pin);
    const next: Session = {
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      deviceId: result.deviceId,
      branchId: result.branchId ?? "",
      branchViewAll: result.branchViewAll ?? false,
      account: result.account,
    };
    saveSession(next);
    touchScreenUnlock(result.account.id); // login disengaja = mulai jendela tak-terkunci
    setSession(next);
    setStatus("authenticated");
    return next;
  }, []);

  // Ganti cabang aktif (RFC-007 §5): server re-otorisasi & terbitkan token
  // baru dgn cabang baru. Simpan sesi -> komponen yang bergantung token
  // (katalog, shift) otomatis reload karena token berubah.
  const switchBranchTo = useCallback(async (branchId: string) => {
    const current = loadSession();
    if (!current) return;
    const result = await apiSwitchBranch(current.accessToken, branchId);
    const next: Session = {
      ...current,
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      deviceId: result.deviceId || current.deviceId,
      branchId: result.branchId,
      branchViewAll: result.branchViewAll,
      branchCode: undefined, // diisi ulang oleh BranchProvider dari daftar cabang
    };
    saveSession(next);
    setSession(next);
  }, []);

  const setBranchCode = useCallback((code: string) => {
    setSession((prev) => {
      if (!prev || prev.branchCode === code) return prev;
      const next = { ...prev, branchCode: code };
      saveSession(next);
      return next;
    });
  }, []);

  const signOut = useCallback(() => {
    clearSession();
    clearScreenUnlock();
    setSession(null);
    setStatus("anonymous");
  }, []);

  // Refresh token: tukar refresh token dgn access+refresh baru (rotasi).
  // Dipakai api.ts saat access token kedaluwarsa, agar pengguna tak ditendang
  // setiap token akses habis. Baca dari storage agar pakai refresh terbaru.
  const refresh = useCallback(async (): Promise<string | null> => {
    const current = loadSession();
    if (!current?.refreshToken) return null;
    try {
      const result = await refreshSession(current.refreshToken);
      const next: Session = {
        ...current,
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        deviceId: result.deviceId,
      };
      saveSession(next);
      setSession(next);
      return result.accessToken;
    } catch {
      return null;
    }
  }, []);

  // Daftarkan handler refresh ke api.ts + tangani sesi kedaluwarsa: saat
  // refresh juga gagal, api.ts memancarkan "pos:unauthorized" → keluar &
  // router otomatis mengarahkan ke /login.
  useEffect(() => {
    setRefreshHandler(refresh);
    const onUnauthorized = () => {
      if (!loadSession()) return; // bukan sesi aktif (mis. gagal login biasa)
      clearSession();
      setSession(null);
      setStatus("anonymous");
      toast.error("Sesi berakhir. Silakan masuk lagi.");
    };
    window.addEventListener("pos:unauthorized", onUnauthorized);
    return () => {
      setRefreshHandler(null);
      window.removeEventListener("pos:unauthorized", onUnauthorized);
    };
  }, [refresh]);

  const can = useCallback(
    (permission: string) => {
      if (!session) return false;
      // Owner = wildcard SEMUA permission, KECUALI storage.manage (manajemen
      // file S3) yang khusus developer (Pemilik pun tak boleh). Developer &
      // role lain mengandalkan daftar permission efektif dari server.
      if (session.account.role === "owner") return permission !== "storage.manage";
      return session.account.permissions.includes(permission);
    },
    [session],
  );

  const value = useMemo<AuthValue>(
    () => ({
      status,
      account: session?.account ?? null,
      token: session?.accessToken ?? "",
      // Developer = superset Pemilik → diperlakukan owner-equivalent untuk UI.
      isOwner: session?.account.role === "owner" || session?.account.role === "developer",
      branchId: session?.branchId ?? "",
      branchViewAll: session?.branchViewAll ?? false,
      branchCode: session?.branchCode ?? "",
      signIn,
      signOut,
      switchBranch: switchBranchTo,
      setBranchCode,
      can,
    }),
    [status, session, signIn, signOut, switchBranchTo, setBranchCode, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() harus dipakai di dalam <AuthProvider>");
  return ctx;
}
